<template src="./review-form.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import bu from 'util/browser-utils'
import { callOnReviewSubmit } from 'util/turnto-utils'
import domUtils from 'util/dom-utils'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import globalConfig from 'init/global.config'
import PageLoadingMixin from 'mixins/PageLoadingMixin.vue'
import reviewSubmissionConfig from 'widgets/submission/review-submission.config'
import submissionStore, { SubmissionPage } from 'store/submission-store'
import AddedMedia from './visual/AddedMedia.vue'
import CheckGroup from 'objects/form/CheckGroup.vue'
import { DoMoreProps, SellerRatingMessageType } from 'util/interfaces'
import FieldGroup from 'objects/form/FieldGroup.vue'
import RatingStarsField from 'components/rating/RatingStarsField.vue'
import ReviewDimensionFieldset from './ReviewDimensionsFieldset.vue'
import ReviewFormHeader from './ReviewFormHeader.vue'
import ReviewFlowProduct from './ReviewFlowProduct.vue'
import { RestError, ReviewInitData, ReviewInboxFormData } from 'services/api-service'
import reviewInitDataService from 'services/api/review-init-data'
import rseReviewInitDataService from 'services/api/rse-review-init-data'
import saveReviewConfirmedService from 'services/api/save-review-confirmed'
import saveReviewRseService from 'services/api/save-review-rse'
import saveReviewUnconfirmedService from 'services/api/save-review-unconfirmed'
import ShopperProfileFieldset, { FREE_FORM_TEXT_TYPE, SINGLE_SELECT_TYPE }
  from 'components/shopper-profile/ShopperProfileFieldset.vue'
import srUtils from 'util/seller-rating-utils'
import ssoService from 'services/sso-service'
import Textarea from 'objects/form/Textarea.vue'
import TextField from 'objects/form/TextField.vue'
import tokenService from 'services/token-service'
import { MAX_MEDIA, TYPE_PHOTO } from 'util/media-utils'


interface FieldValidation {
  canBeFalse?: boolean
  minLength?: number
  maxLength?: number
  name: string
  required?: boolean
}

const DEFAULT_VALUES =  JSON.stringify({
  catItemId: 0,
  acceptedTerms: false,
  deviceFingerprint: null,
  dimensions: [],
  fromMobile: false,
  incentiveType: null,
  media: [],
  profile: { customAttributes: [] },
  rating: 0,
  recommend: null,
  source: null,
  sweepstakesOptIn: false,
  text: '',
  title: ''
})

let deviceFingerprintService

export default {
  name: 'review-form',
  data() {
    return {
      dimensionDefinitions: [],
      dimensionsModel: {},
      dimensionErrors: {},
      fieldErrors: {},
      // the page before the review form. Not including add-video or add-captions
      fromPage: undefined,
      globalConfig,
      guidelinesOpen: false,
      hasInboxErrors: false,
      isSubmitting: false,
      purchasedItem: undefined,
      profileErrors: { customAttributes: {} },
      submissionStoreState: submissionStore.state,
      profileDefinitions: {},
      shopperProfileModel: {},
      values: JSON.parse(DEFAULT_VALUES),
      eriHeaderMessages: [],
      isEriCampaignActive: false
    }
  },
  props: {
    checkoutSiteKey: String, // from do-more if from sibling site (multi-store)
    // Passed from add-video or add-captions to focus on button that launched those screens
    // value is media type TYPE_PHOTO or TYPE_VIDEO
    mediaBtnFocus: Number,
    // Passed in from add-captions screen when only one image and its invalid or failed upload
    mediaError: String
  },
  emits: ['showError'],
  components: {
    'tt-rating-stars-field': RatingStarsField,
    'tt-check-group': CheckGroup,
    'tt-field-group': FieldGroup,
    'tt-review-dimension-fieldset': ReviewDimensionFieldset,
    'tt-review-form-header': ReviewFormHeader,
    'tt-review-flow-product': ReviewFlowProduct,
    'tt-added-media': AddedMedia,
    'tt-shopper-profile-fieldset': ShopperProfileFieldset,
    'tt-textarea': Textarea,
    'tt-text-field': TextField
  },
  mixins: [PageLoadingMixin],
  computed: {
    addedMedia() {
      return this.submissionStoreState.addedMedia
    },
    catItem() {
      return this.submissionStoreState.catItem
    },
    fieldProperties() {
      return {
        text: {
          errorText: this.fieldErrors['text'],
          maxCharacters: reviewSubmissionConfig.textMaxLength
        }
      }
    },
    fieldValidations() {
      const validations = [
        { name: 'rating', required: true },
        { name: 'text', required: reviewSubmissionConfig.textRequired,
          minLength: reviewSubmissionConfig.textMinLength,
          maxLength: reviewSubmissionConfig.textMaxLength },
        { name: 'title', required: reviewSubmissionConfig.titleRequired,
          maxLength: reviewSubmissionConfig.titleMaxLength }
      ] as Array<FieldValidation>
      if (reviewSubmissionConfig.recommendRequired) {
        validations.push({ name: 'recommend', required: true, canBeFalse: true })
      }
      if (reviewSubmissionConfig.acceptTermsRequired) {
        validations.push({ name: 'acceptedTerms', required: true, canBeFalse: false })
      }
      return validations
    },
    hasErrors() {
      return Object.keys(this.fieldErrors).length > 0 ||
        Object.keys(this.dimensionErrors).length > 0 ||
        Object.keys(this.profileErrors).length > 1 ||
        Object.keys(this.profileErrors.customAttributes).length > 0
    },
    heading() {
      if (this.launchData.startAction === 'visual') {
        return this.$msg('revForm.heading.addReview')
      } else if (this.purchasedItem) {
        return this.$msg('revForm.heading.purchased')
      }
      return this.$msg('revForm.heading.default')
    },
    isVisualFirst() {
      return submissionStore.isVisualFirstFlow()
    },
    launchData() {
      return this.submissionStoreState.launchData
    },
    recommendFields() {
      return [
        { label: this.$msg('revForm.recommend.yes'), iconName: 'check', value: true },
        { label: this.$msg('revForm.recommend.no'), iconName: 'close', value: false }
      ]
    },
    reviewSubmissionConfig() {
      return reviewSubmissionConfig
    },
    siblingSiteKey() {
      return submissionStore.siblingSiteKey
    },
    srTransId() { // seller rating transaction id
      return this.submissionStoreState.sellerRatingLaunchData.transactionId
    },
    eriCampaignMessage() {
      return this.eriHeaderMessages.join('<br/>')
    }
  },
  methods: {
    addPhotos(files: File[]) {
      if ((submissionStore.addedMedia.length + files.length) > MAX_MEDIA) {
        this.fieldErrors.media = this.$msg('revForm.tooManyPhotos', [MAX_MEDIA])
      } else if (files.length > 0) {
        submissionStore.currentPage = {
          page: 'add-captions',
          props: { imageFiles: files }
        } as SubmissionPage
      }
    },
    addVideo() {
      submissionStore.currentPage = { page: 'add-video', props: {} } as SubmissionPage
    },
    back() {
      if (this.fromPage) {
        const props = this.fromPage === 'do-more' ? { msgType: 'default' } as DoMoreProps : {}
        submissionStore.currentPage = { page: this.fromPage, props }
        return
      }
      submissionStore.close()
    },
    clearErrors() {
      this.fieldErrors = {}
      this.dimensionErrors = {}
      this.profileErrors = { customAttributes: {} }
    },
    fetchInitData() {
      // if using device fingerprint, make sure the device fingerprint module is loaded
      if (reviewSubmissionConfig.deviceFingerprintOn) {
        // eslint-disable-next-line max-len
        import(/* webpackChunkName: 'dfp' */ 'services/device-fingerprint').then((fingerprintService) => {
          deviceFingerprintService = fingerprintService.default
        })
      }
      if (this.launchData.rseToken) {
        rseReviewInitDataService.rseReviewInitData({
          'context-token': this.launchData.rseToken,
          ssoProfileToken: this.launchData.ssoProfileToken || null,
          sku: submissionStore.sku,
          checkoutSiteKey: submissionStore.siblingSiteKey,
          lastSeenSrDate: srUtils.getLastSeenSellerRatingDate(),
          campaign: this.launchData.campaign || null,
          incentiveType: this.launchData.incentiveType || null
        }).then((data) => {
          if (data.token) {
            tokenService.loginUser(data.token, data.userData)
          }
          if (this.launchData.ssoProfileToken && data.token) {
            // if the user is client logged in and widgetServer returns a token, that means the
            // user identity does not match and should be logged out
            ssoService.logout()
          }
          this.initializeForm(data)
        }).catch((e: RestError) => {
          this.$emit('showError', getOfflineOrUnexpectedMsg(e), true)
          this.hasInboxErrors = false
        })
      } else if (this.launchData.fromSso) {
        reviewInitDataService.reviewInitData({
          'context-token': this.launchData.ssoToken,
          checkoutSiteKey: submissionStore.siblingSiteKey,
          sku: submissionStore.sku
        }).then((data) => {
          this.initializeForm(data)
        }).catch((e: RestError) => {
          this.$emit('showError', getOfflineOrUnexpectedMsg(e), true)
          this.hasInboxErrors = false
        })
      } else {
        reviewInitDataService.reviewInitData({
          'context-token': tokenService.getUserToken() || '',
          checkoutSiteKey: submissionStore.siblingSiteKey,
          sku: submissionStore.sku,
          ssoProfileToken: this.launchData.ssoProfileToken
        }).then((data) => {
          this.initializeForm(data)
        }).catch((e: RestError) => {
          if (e.errorCode === 'token-error') {
            // invalid token, need to logout user and try again as unconfirmed
            tokenService.logoutUser()
            this.fetchInitData()
            return
          }
          this.$emit('showError', getOfflineOrUnexpectedMsg(e), true)
          this.hasInboxErrors = false
        })
      }
    },
    goToDoMore(props = {}) {
      submissionStore.currentPage = { page: 'do-more', props } as SubmissionPage
    },
    goToSellerRating(msgType: SellerRatingMessageType) {
      submissionStore.currentPage = {
        page: 'seller-rating-form',
        props: { msgType }
      } as SubmissionPage
    },
    handleErrors(e: RestError) {  // review submission errors
      // backend validation errors in case not caught for some reason in front-end
      // doesn't currently handle dimension and profile attribute errors
      this.clearErrors()
      if (e.fieldErrors) {
        e.fieldErrors.forEach((fieldErr) => {
          if (fieldErr.code === 'invalid-rating') {
            this.fieldErrors['rating'] = this.$msg('revForm.rating.err.required')
          } else if (fieldErr.code === 'required' && fieldErr.path === 'title') {
            this.fieldErrors['title'] = this.$msg('revForm.title.err.required')
          } else if (fieldErr.code === 'max-size' && fieldErr.path === 'title') {
            this.fieldErrors['title'] = this.$msg('revForm.title.err.maxLength',
              [reviewSubmissionConfig.titleMaxLength], reviewSubmissionConfig.titleMaxLength)
          } else if (fieldErr.code === 'min-size' && fieldErr.path === 'text') {
            this.fieldErrors['text'] =
              this.$msg('revForm.text.err.minLength',
                [reviewSubmissionConfig.textMinLength], reviewSubmissionConfig.textMinLength)
          } else if (fieldErr.code === 'max-size' && fieldErr.path === 'text') {
            this.fieldErrors['text'] =
              this.$msg('revForm.text.err.maxLength',
                [reviewSubmissionConfig.textMaxLength], reviewSubmissionConfig.textMaxLength)
          } else if (fieldErr.code === 'ext-site-link') {
            if (fieldErr.path.indexOf('customAttributes[') === 0) {
              const index = parseInt(fieldErr.path.substring(17, fieldErr.path.length - 1))
              this.profileErrors.customAttributes[index] = this.$msg('revForm.err.extLink')
            } else {
              // title or text
              this.fieldErrors[fieldErr.path] = this.$msg('revForm.err.extLink')
            }
          } else if (fieldErr.code === 'required' && fieldErr.path === 'acceptedTerms') {
            this.fieldErrors['acceptedTerms'] = this.$msg('revForm.acceptedTerms.err.required')
          }
        })
        if (this.hasErrors) {
          this.isSubmitting = false
          this.scrollToTopError()
          return
        }
      }
      // bubbles up error message (submission-mediator -> submission-modal)
      const errorMsgKey = e.errorCode === 'item-already-reviewed'
        ? 'revForm.alreadyReviewed.message'
        : e.errorCode === 'turnto-offline' ? 'offline.error' : 'unexpected.error'
      this.$emit('showError', this.$msg(errorMsgKey))
      this.isSubmitting = false
    },
    hasValue(value) {
      // used for checking dimensions and profile attributes for a value
      const isArray = Array.isArray(value)
      return (typeof value === 'string' && value.trim().length > 0)
        || (value && !isArray)
        || (isArray && value.length > 0)
    },
    initializeForm(data: ReviewInitData) {
      if (data.sellerRatingTransactionId) {
        submissionStore.sellerRatingLaunchData = { transactionId: data.sellerRatingTransactionId }
      }
      if (data.alreadyReviewed) {
        if (this.launchData.ssoProfileToken) { // TT-12317. will change to be more elegant later
          this.$emit('showError', this.$msg('revForm.alreadyReviewed.message'), true)
          return
        }
        this.goToDoMore({ msgType: 'alreadyReviewed' } as DoMoreProps)
        return
      }
      if (globalConfig.isPurchaseReq()) {
        const userToken = tokenService.getUserToken()
        if (!userToken && !this.launchData.rseToken && !this.launchData.fromSso) {
          this.showRegistration()
          return
        }
        if ((userToken || this.launchData.ssoToken) &&
          data.purchasedItem !== undefined && !data.purchasedItem) {
          this.goToDoMore({ msgType: 'purchaseRequired' } as DoMoreProps)
          return
        }
      }
      // add catItem to store so it can be referenced by this and other screens in the flow
      submissionStore.catItem = data.catItem
      this.values.catItemId = data.catItem.id // to compare against store when sku is changed
      this.purchasedItem = data.purchasedItem
      this.dimensionDefinitions = data.dimensionDefinitions || []
      // if the user had media previously sumbitted for this sku, it is automatically being added
      if (data.userMedia) {
        submissionStore.addMedia(data.userMedia)
      }
      if (data.shopperProfile && data.shopperProfile.definitions) {
        this.profileDefinitions = data.shopperProfile.definitions
      }
      if (data.shopperProfile && data.shopperProfile.values &&
        Object.keys(data.shopperProfile.values).length > 0) {
        this.shopperProfileModel = Object.assign(
          data.shopperProfile.values, { ageRangeIsDirty: false, locationIsDirty: false }
        )
      }
      if (data.eriCampaign) {
        if (!data.eriCampaign.active) {
          this.eriHeaderMessages.push(this.$msg('eri.campaign.inactive'))
        } else {
          this.isEriCampaignActive = true
          data.eriCampaign.incentives.forEach((incentive) => {
            const type = incentive.ugcTier === 1 ? 'reviewType' : 'reviewAndPhotoType'
            const incentiveMsg = this.$msg('eri.campaign.incentive.' + type, [incentive.name])
            this.eriHeaderMessages.push(incentiveMsg)
          })
        }
      }
      this.setInboxValues(data.inboxSubmissionFormData)
      // calling PageLoadingMixin
      this.pageLoaded(true)
    },
    scrollToTopError() {
      // now scroll to first error (if any)
      nextTick(() => {
        const firstError = this.$el.querySelector('.tt-o-hint--error')
        if (firstError) {
          const fieldGroup = domUtils.closest(firstError, '.tt-o-field-group')
          // this is here for smooth scrolling
          domUtils.scrollToElement(fieldGroup)
          // setTimeout to wait for smooth scroll up before focusing on field
          setTimeout(() => {
            const formFieldSelectors = 'input, select, textarea, [role=radio], [role=slider]'
            domUtils.safeFocus(
              fieldGroup.querySelector(formFieldSelectors) as HTMLElement
            )
          }, 500)
        }
      })
    },
    setDimensionValuesForSubmit() {
      // clear out dimensions that may have been set on previous submit attempt
      this.values.dimensions = []
      // Only submit dimensions that have a value
      Object.keys(this.dimensionsModel).forEach((dimId) => {
        const value = this.dimensionsModel[dimId]
        if (this.hasValue(value)) {
          this.values.dimensions.push({ id: dimId, value })
        }
      })
    },
    setInboxValues(inboxSubmissionFormData: ReviewInboxFormData) {
      if (this.hasInboxErrors) {
        this.hasInboxErrors = false
        Object.keys(inboxSubmissionFormData).forEach((field) => {
          if (field === 'dimensions') {
            this.dimensionsModel = {}
            if (inboxSubmissionFormData.dimensions) {
              inboxSubmissionFormData.dimensions.forEach((dimValue) => {
                this.dimensionsModel[dimValue.id] =
                  typeof dimValue.value === 'string' ? parseInt(dimValue.value) : dimValue.value
              })
            }
          } else {
            if (inboxSubmissionFormData[field] === null) {
              this.values[field] = field === 'rating' ? 0 : ''
              return
            }
            this.values[field] = inboxSubmissionFormData[field]
          }
        })
        // let it finish populating the values on the screen
        nextTick(() => { this.validate() })
      }
    },
    setProfileValuesForSubmit() {
      // move profile values from model into values, remove any attributes that don't have a value
      this.values.profile = JSON.parse(JSON.stringify(this.shopperProfileModel))
      if (this.values.profile && this.values.profile.customAttributes) {
        this.values.profile.customAttributes =
          this.values.profile.customAttributes.filter((attrVal) => {
            if (!attrVal.valueDefinitionId) {
              return false
            }
            const type = this.profileDefinitions.customAttributes.find((customAttr) => {
              return customAttr.id === attrVal.id
            }).type
            return type === SINGLE_SELECT_TYPE ||
              (type === FREE_FORM_TEXT_TYPE && attrVal.freeFormText)
          })
      }
    },
    showRegistration(stagedContentToken = null) {
      const props = {
        registerFor: 'review',
        stagedContentToken
      }
      submissionStore.currentPage = { page: 'registration', props } as SubmissionPage
    },
    skip() {
      this.srTransId ?
        this.goToSellerRating('noReview' as SellerRatingMessageType) :
        this.goToDoMore({ msgType: 'vc' } as DoMoreProps)
    },
    submit() {
      this.isSubmitting = true
      this.validate()
      if (this.hasErrors) {
        this.isSubmitting = false
        return
      }
      this.values.fromMobile = bu.isMobileDevice()
      // since the "src" field is added to images in add-captions.ts, need to remove before
      // sending in call. Just going to do deep copy and remove, so we don't affect the
      // media with "src" that may still need to be used
      this.values.media = JSON.parse(JSON.stringify(this.addedMedia)).map((mediaItem) => {
        delete mediaItem.src
        return mediaItem
      })
      this.setDimensionValuesForSubmit()
      this.setProfileValuesForSubmit()
      if (reviewSubmissionConfig.deviceFingerprintOn && deviceFingerprintService) {
        this.values.deviceFingerprint = deviceFingerprintService.getDeviceFingerprint()
      }
      this.values.source = this.launchData.source
      const userToken = tokenService.getUserToken()
      if (this.launchData.rseToken) {
        saveReviewRseService.saveReviewRse(
          {
            'context-token': this.launchData.rseToken,
            checkoutSiteKey: this.siblingSiteKey,
            sku: submissionStore.sku,
            campaign: this.launchData.campaign || null,
            incentiveType: this.launchData.incentiveType || null
          },
          this.values
        ).then((data) => {
          this.srTransId && !data.incentivized ?
            this.goToSellerRating('reviewed' as SellerRatingMessageType) :
            this.goToDoMore({ msgType: 'review' } as DoMoreProps)
          callOnReviewSubmit(data.reviewId)
          this.isSubmitting = false
        }).catch(e => this.handleErrors(e))
      } else if (userToken || this.launchData.fromSso) {
        saveReviewConfirmedService.saveReviewConfirmed(
          {
            // with soft-login (TT-12125), user token takes precedence over ssoToken
            'context-token': userToken ? userToken : this.launchData.ssoToken,
            checkoutSiteKey: submissionStore.siblingSiteKey,
            sku: submissionStore.sku
          },
          this.values
        ).then((data) => {
          this.goToDoMore({ msgType: 'review' } as DoMoreProps)
          callOnReviewSubmit(data.reviewId)
          this.isSubmitting = false
        }).catch(e => this.handleErrors(e))
      } else {
        saveReviewUnconfirmedService.saveReviewUnconfirmed({
          checkoutSiteKey: submissionStore.siblingSiteKey,
          sku: submissionStore.sku
        }, this.values).then((data) => {
          if (globalConfig.ssoEnabled) {
            ssoService.ssoCreateReview(
              submissionStore.sku, this.values.source, data.stagedReviewToken
            )
            submissionStore.isOpen = false
          } else {
            // confirmation message shown after and by registration component
            this.showRegistration(data.stagedReviewToken)
          }
          this.isSubmitting = false
        }).catch(e => this.handleErrors(e))
      }
    },
    validate() {
      this.clearErrors()
      this.validateStandardFields()
      this.validateDimensions()
      this.validateShopperProfile()
      this.scrollToTopError()
    },
    validateDimensions() {
      this.dimensionDefinitions.forEach((dimDef) => {
        const value = this.dimensionsModel[dimDef.id]
        if (dimDef.required && !this.hasValue(value)) {
          const msgType = dimDef.type === 1 ? 'Rating' : 'Option'
          this.dimensionErrors[dimDef.id] =
            this.$msg(`revForm.customDim${msgType}.err.required`)
        }
      })
    },
    validateShopperProfile() {
      const ageRange = this.profileDefinitions.ageRange
      if (ageRange && ageRange.required && !this.shopperProfileModel.ageRange) {
        this.profileErrors.ageRange = this.$msg('profileForm.ageRange.err.required')
      }
      const location = this.profileDefinitions.location
      if (location && location.required) {
        ['country', 'city', 'state'].forEach((locationType) => {
          if (!this.shopperProfileModel[locationType]) {
            const errorFieldName = locationType === 'state' ? 'state' : locationType
            this.profileErrors[locationType] =
              this.$msg(`profileForm.${errorFieldName}.err.required`)
          }
        })
      }
      if (this.profileDefinitions.customAttributes) {
        const customAttrValues = this.shopperProfileModel.customAttributes
        this.profileDefinitions.customAttributes.forEach((customAttr, i) => {
          const value = customAttr.type === 1 ? customAttrValues[i].freeFormText
            : customAttrValues[i].valueDefinitionId
          if (customAttr.required && !this.hasValue(value)) {
            const msgType = customAttr.type === 1 ? 'Text' : 'Option'
            const msgKey = `profileForm.profileAttribute${msgType}.err.required`
            this.profileErrors.customAttributes[customAttr.id] = this.$msg(msgKey)
          }
        })
      }
    },
    validateStandardFields() {
      this.fieldValidations.forEach((validation) => {
        const name = validation.name
        let value = this.values[validation.name]
        if (value && typeof value === 'string') {
          value = value.trim()
          this.values[validation.name] = value
        }
        if (validation.required && !validation.minLength && !value &&
          !(validation.canBeFalse && value === false)) {
          this.fieldErrors[name] = this.$msg(`revForm.${name}.err.required`)
        } else if (validation.minLength && (!value || value.length < validation.minLength)) {
          this.fieldErrors[name] =
            this.$msg(`revForm.${name}.err.minLength`, [validation.minLength], validation.minLength)
        } else if (validation.maxLength && value && value.length > validation.maxLength) {
          this.fieldErrors[name] =
            this.$msg(`revForm.${name}.err.maxLength`, [validation.maxLength], validation.maxLength)
        }
      })
    },
    activated() {
      // event fired from wrapping component (submission modal) when the user clicks modal close
      // or presses esc
      submissionStore.eventBus.on('back', this.back)
      // media error passed from captions screen, show it
      if (this.mediaError) {
        this.fieldErrors.media = this.mediaError
        nextTick(() => this.scrollToTopError())
      } else {
        // make sure any previous errors are cleared
        delete this.fieldErrors.media
      }
      // TT-11851 - should focus on media button when coming back from add-video or add-captions,
      // focus on "hide guidelines" when selected from inbox, otherwise allow submission-modal
      // to keep the focus on the modal content area
      let focusSelector = undefined
      if (this.mediaBtnFocus !== undefined) {
        focusSelector = this.mediaBtnFocus === TYPE_PHOTO ? '.tt-c-added-media__add-photo-btn' :
          '.tt-c-added-media__add-video'
      } else if (this.guidelinesOpen) {
        focusSelector = '.tt-c-review-form-header__guidelines-link'
      }
      if (focusSelector) {
        domUtils.safeFocus(() => this.$el.querySelector(focusSelector))
      }

      // need to track the page before, so can go back.  This does not include add-captions or
      // the review-form itself.  add-video is not included in page history
      const prevPage = submissionStore.previousPage ? submissionStore.previousPage.page : undefined
      this.fromPage =
        prevPage !== 'add-captions' && prevPage !== 'review-form' ? prevPage : undefined

      // if the cat item has changed or set for the 1st time,
      // make sure the form is clear and get the init data
      const storeCatItem = submissionStore.catItem
      if (!storeCatItem || this.values.catItemId !== storeCatItem.id) {
        this.values = JSON.parse(DEFAULT_VALUES)
        this.dimensionsModel = {}
        this.eriHeaderMessages = []
        this.isEriCampaignActive = false
        this.fetchInitData()
      } else {
        // calling PageLoadingMixin
        this.pageLoaded(true)
      }
      // if a star value came from do more or email, set as initial value (if not already set)
      if (this.values.rating === 0 && this.launchData.starSelected) {
        this.values.rating = this.launchData.starSelected
      }
      this.values.incentiveType = this.launchData.incentiveType || null
    }
  },
  // activated lifecycle event is fired because it is a keep-alive component
  activated() {
    this.activated()
  },
  created() {
    if (this.launchData.showGuidelines) {
      this.guidelinesOpen = true
    }
    this.hasInboxErrors = this.launchData.hasErrors ? true : false
  }
}
</script>

<style lang="scss" src="./review-form.scss"></style>
